import PropTypes from "prop-types";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  BaseForm,
  buildCustomFieldSelection,
  CustomFieldsSection,
} from "components/BaseForm";
import { formatError } from "helpers/formatError";
import { useCampaignMutations } from "hooks/useCampaign";
import { SUCCESS_MESSAGES } from "routes/campaign/form/constants";
import { buildDefaultValues, FORM_MODES } from "routes/campaign/form/defaults";
import { toCampaignPayload } from "routes/campaign/form/payload";
import { buildCampaignSchema } from "routes/campaign/form/schema";
import DetailsSection from "routes/campaign/form/sections/DetailsSection";
import EmailSection from "routes/campaign/form/sections/EmailSection";
import PresetsSection from "routes/campaign/form/sections/PresetsSection";
import { useCampaignFormOptions } from "routes/campaign/forms/useCampaignFormOptions";

/**
 * The Campaign create and edit form. Its caller loads the record and the
 * custom field configs before mounting it, because BaseForm reads the defaults
 * once. The option lists may load later.
 */
const CampaignForm = ({
  mode,
  record,
  customFieldConfigs,
  customFieldGroups = [],
  onSaved,
  onCancel,
}) => {
  const { create, update } = useCampaignMutations();
  const savedRef = useRef(null);
  const isEdit = mode === FORM_MODES.edit;
  const options = useCampaignFormOptions(record?.assigned_user_id ?? null);

  const [customFieldSelection, setCustomFieldSelection] = useState(() =>
    buildCustomFieldSelection(
      customFieldConfigs,
      isEdit ? (record.custom_fields ?? {}) : null
    )
  );

  const schema = useMemo(
    () => buildCampaignSchema({ customFieldConfigs }),
    [customFieldConfigs]
  );

  const defaultValues = useMemo(
    () => buildDefaultValues({ mode, record, customFieldConfigs }),
    [mode, record, customFieldConfigs]
  );

  const handleSubmit = async values => {
    const data = toCampaignPayload(values, {
      customFieldConfigs,
      campaignId: record?.id,
    });
    try {
      savedRef.current = isEdit
        ? await update.mutateAsync(data)
        : await create.mutateAsync(data);
    } catch (error) {
      // BaseForm shows the banner and field errors from the rethrown error.
      toast.error(formatError(error, "Could not save the Campaign"));
      throw error;
    }
  };

  const handleSuccess = () => {
    toast.success(SUCCESS_MESSAGES[mode]);
    onSaved(savedRef.current);
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      onCancel={onCancel}
    >
      <div className="tw:flex tw:flex-col tw:gap-6">
        <DetailsSection
          statusOptions={options.statusOptions}
          userOptions={options.userOptions}
          formOptions={options.formOptions}
        />
        <EmailSection
          emailServiceOptions={options.emailServiceOptions}
          emailTypeOptions={options.emailTypeOptions}
          templateOptions={options.templateOptions}
        />
        <PresetsSection
          regionOptions={options.regionOptions}
          campaignStatusPresetOptions={options.campaignStatusPresetOptions}
          revenueStatusPresetOptions={options.revenueStatusPresetOptions}
          revenueRecipientPresetOptions={options.revenueRecipientPresetOptions}
        />
        <CustomFieldsSection
          selection={customFieldSelection}
          setSelection={setCustomFieldSelection}
          groups={customFieldGroups}
        />
      </div>
    </BaseForm>
  );
};

CampaignForm.propTypes = {
  mode: PropTypes.oneOf(Object.values(FORM_MODES)).isRequired,
  record: PropTypes.object,
  customFieldConfigs: PropTypes.array.isRequired,
  customFieldGroups: PropTypes.array,
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
};

export default CampaignForm;
